/**
 * Scolarité: students, classes, the teacher's daily loop, the calendar
 * and the class-control surfaces.
 *
 * Part of the route table (see ./index.ts). Split by domain so adding a
 * screen touches one small file instead of one long one.
 */
import { lazy } from "react";
import type { AppRoute } from "./types";

export const ACADEMICS_ROUTES: AppRoute[] = [
  {
    path: "/students",
    title: "Élèves",
    component: lazy(() => import("@/pages/students/StudentsPage")),
    perms: ["people.student.list", "people.student.view"],
    nav: { label: "Élèves", order: 2, mobile: 2, group: "Scolarité" },
  },
  {
    path: "/classes",
    title: "Classes",
    component: lazy(() => import("@/pages/classes/ClassesPage")),
    perms: ["academics.class.list", "academics.class.view"],
    nav: { label: "Classes", order: 3, group: "Scolarité" },
  },
  {
    path: "/teacher",
    title: "Accueil enseignant",
    component: lazy(() => import("@/pages/teacher/TeacherHomePage")),
    perms: ["academics.attendance.record", "academics.exam.grade.enter"],
    nav: { label: "Ma journée", order: 2.5, group: "Scolarité" },
  },
  {
    path: "/attendance",
    title: "Appel",
    component: lazy(() => import("@/pages/attendance/AttendancePage")),
    perms: ["academics.attendance.record", "academics.attendance.view.class"],
    nav: { label: "Appel", order: 3.1, mobile: 6, group: "Scolarité" },
  },
  {
    path: "/grades",
    title: "Saisie des notes",
    component: lazy(() => import("@/pages/grades/GradesPage")),
    perms: ["academics.exam.grade.enter", "academics.exam.grade.edit"],
    nav: { label: "Notes", order: 3.2, mobile: 7, group: "Scolarité" },
  },
  {
    path: "/homework",
    title: "Cahier de textes",
    component: lazy(() => import("@/pages/homework/HomeworkPage")),
    perms: ["academics.homework.entry.list"],
    nav: { label: "Cahier de textes", order: 3.3, group: "Scolarité" },
  },
  {
    path: "/calendar",
    title: "Calendrier scolaire",
    component: lazy(() => import("@/pages/calendar/CalendarPage")),
    perms: ["academics.calendar.event.list"],
    nav: { label: "Calendrier", order: 3.4, group: "Scolarité" },
  },
  {
    path: "/class-control",
    title: "Contrôle de classe",
    component: lazy(() => import("@/pages/class-control/ClassControlPage")),
    perms: [
      "class_control.view_dashboard",
      "class_control.submit_headcount",
      "class_control.submit_attendance",
      "class_control.report_unknown_student",
    ],
    nav: { label: "Contrôle de classe", order: 3.7, group: "Scolarité" },
  },
  {
    path: "/sessions",
    title: "Mes séances du jour",
    component: lazy(() => import("@/pages/sessions/MySessionsPage")),
    perms: ["class_control.confirm_session"],
    nav: { label: "Mes séances", order: 3.8, group: "Scolarité" },
  },
  {
    path: "/academic-year",
    title: "Année & périodes",
    component: lazy(() => import("@/pages/academic-year/AcademicYearPage")),
    perms: ["academics.year.list", "academics.term.list"],
    nav: { label: "Année scolaire", order: 40, group: "Scolarité" },
  },
  {
    path: "/timetable",
    title: "Emploi du temps",
    component: lazy(() => import("@/pages/timetable/TimetablePage")),
    perms: ["academics.calendar.overview.view"],
    nav: { label: "Emploi du temps", order: 7, group: "Scolarité" },
  },
  {
    path: "/exams",
    title: "Examens & Bulletins",
    component: lazy(() => import("@/pages/exams/ExamsPage")),
    perms: ["academics.exam.session.list", "academics.exam.session.view"],
    nav: { label: "Examens", order: 8, group: "Scolarité" },
  },
  {
    path: "/elections",
    title: "Élections de chef de classe",
    component: lazy(() => import("@/pages/elections/ElectionsPage")),
    perms: ["class_control.view_captain_election_results"],
    nav: { label: "Élections", order: 41, group: "Scolarité" },
  },
  {
    path: "/vote",
    title: "Voter",
    component: lazy(() => import("@/pages/vote/VotePage")),
    perms: ["class_control.vote_class_captain"],
    nav: { label: "Voter", order: 42, group: "Scolarité" },
  },
  {
    path: "/qr-scan",
    title: "Scan des arrivées",
    component: lazy(() => import("@/pages/qr-attendance/QrScanPage")),
    perms: ["qr_attendance.scan", "qr_attendance.manual_mark"],
    nav: { label: "Scan des arrivées", order: 3.9, group: "Scolarité" },
  },
  {
    path: "/qr-history",
    title: "Arrivées du jour",
    component: lazy(() => import("@/pages/qr-attendance/QrHistoryPage")),
    perms: ["qr_attendance.view_history", "qr_attendance.correct_attendance"],
    nav: { label: "Arrivées du jour", order: 3.95, group: "Scolarité" },
  },
];
